import React, { useContext, useState } from 'react';

import { ApiContext } from '_lib/api/apiContext';
import { BadRequestError } from '_lib/api/apiErrors';
import { AiCallDetailDTO, CallOutcomeEnum, FieldIssue } from '_lib/api/apiTypes.shim';
import { BackdropSpinner } from '_lib/components/BackdropSpinner/BackdropSpinner';
import { DialogActions } from '_lib/components/DialogActions/DialogActions';
import { DialogButton } from '_lib/components/DialogButton/DialogButton';
import { DialogContent } from '_lib/components/DialogContent/DialogContent';
import { ErrorDisplay } from '_lib/components/ErrorDisplay/ErrorDisplay';
import { Stack } from '_lib/components/Stack/Stack';
import { CallOutcomeSelect } from '_lib/featureComponents/CallOutcomeSelect/CallOutcomeSelect';
import { DialogWrapper } from '_lib/featureComponents/DialogWrapper/DialogWrapper';
import { AsyncDialogProps, useAsyncDialog } from '_lib/hooks/useAsyncDialog';

type UpdateAiCallDialogProps = AsyncDialogProps & {
  aiCall: AiCallDetailDTO;
};

const UpdateAiCallDialog = (props: UpdateAiCallDialogProps) => {
  const { onClose, aiCall, ...dialogProps } = props;

  const [outcome, setOutcome] = useState<CallOutcomeEnum | undefined>(aiCall.outcome || undefined);
  const [fieldIssues, setFieldIssues] = useState<FieldIssue[]>([]);
  const [nonFieldErrors, setNonFieldErrors] = useState<string[]>([]);

  const api = useContext(ApiContext);

  const updateAiCallMutation = api.aiCalls.useAiCallPatch();

  const handleConfirm = async () => {
    if (!outcome) return;
    if (outcome === aiCall.outcome) {
      onClose({ action: 'CONFIRM' });
      return;
    }
    try {
      await updateAiCallMutation.patch({ id: aiCall.id, data: { outcome } });
      onClose({ action: 'CONFIRM' });
    } catch (err) {
      if (err instanceof BadRequestError) {
        setFieldIssues(err.responseData.fieldIssues.filter((issue) => issue.path?.[0] !== 'nonFieldErrors') || []);
        setNonFieldErrors(err.responseData.nonFieldErrors);
      }
    }
  };

  return (
    <DialogWrapper onClose={onClose} titleText={'Update Outcome'} {...dialogProps}>
      <DialogContent>
        <BackdropSpinner open={updateAiCallMutation.isPending} />
        <Stack spacing={2.5} padding={2} useFlexGap>
          {!!nonFieldErrors?.length && <ErrorDisplay errors={nonFieldErrors} sx={{ borderRadius: 1 }} />}
          <CallOutcomeSelect
            callOutcome={outcome}
            onSetCallOutcome={(outcome) => setOutcome(outcome)}
            issues={fieldIssues}
            issuePath={'outcome'}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <DialogButton onClick={handleConfirm} text='Confirm' disabled={updateAiCallMutation.isPending || !outcome} />
      </DialogActions>
    </DialogWrapper>
  );
};

export const useUpdateAiCallDialog = () => useAsyncDialog(UpdateAiCallDialog);
